// ═══════════════════════════════════════════════════════
// APP.JSX — Root component (refactored Sprint 9)
// ═══════════════════════════════════════════════════════
// All page components, UI primitives, and shared utilities
// have been extracted to dedicated modules under:
//   src/pages/          — page-level components
//   src/components/ui/  — Card, Badge, Tabs, etc.
//   src/shared/         — theme, formatters, hooks, CSS, benchmarks, PDF, risk
//   src/components/     — Sidebar, ErrorBoundary, RelatorioPDFModal, etc.
// ═══════════════════════════════════════════════════════

import { useState, useEffect, useRef, lazy, Suspense } from "react";
import {
  supabase,
  signOut,
  fetchProfile,
  fetchSuitability,
  upsertSuitability,
  markOnboardingDone,
  logAudit,
  invokeRemoveMustChangePassword,
  logSuitabilityHistory,
} from "./supabaseClient.js";
import { createNotification } from "./services/notifications.ts";
import { LIGHT, DARK } from "./shared/theme.js";
import { logWarn } from "./shared/logger.js";
import { injectGlobalStyles, applyThemeCSSVars, detectSystemTheme } from "./shared/globalStyles.js";
import { BrowserRouter } from "./router/index.js";
import ErrorBoundary from "./components/ErrorBoundary.jsx";
import OnboardingWizard from "./shared/OnboardingWizard.jsx";
import ChangePasswordScreen from "./auth/ChangePasswordScreen.jsx";
import TwoFactorSetup from "./components/TwoFactorSetup.tsx";
import TwoFactorVerify from "./components/TwoFactorVerify.tsx";
import ClientLayout from "./pages/ClientLayout.jsx";
// Sprint UX 2.5 — Onboarding tour cliente (CL-P0-1)
import LpaOnboardingTour from "./components/onboarding/LpaOnboardingTour.tsx";
import LGPDConsentBanner from "./components/LGPDConsentBanner.jsx";

// ── Page components (eager — leves / essenciais na entrada) ──
import LoginPage from "./pages/LoginPage.jsx";
import UploadPromptScreen from "./pages/UploadPromptScreen.jsx";
import { EmptyStateDashboard, EmptyStatePage } from "./pages/EmptyStates.jsx";

// ── Lazy pages — só baixam quando acessados ──────────────
// lazyRetry: on chunk load failure (stale deploy), reload once to get fresh HTML
const lazyRetry = (importFn) =>
  lazy(() =>
    importFn().catch(() => {
      const hasReloaded = sessionStorage.getItem("chunk_reload");
      if (!hasReloaded) {
        sessionStorage.setItem("chunk_reload", "1");
        window.location.reload();
        return new Promise(() => {}); // never resolves — page is reloading
      }
      sessionStorage.removeItem("chunk_reload");
      return importFn(); // second attempt — let it fail naturally
    })
  );

const DashboardPage = lazyRetry(() => import("./pages/DashboardPage.jsx"));
const CarteiraPage = lazyRetry(() => import("./pages/CarteiraPage.tsx"));
const RebalancingSection = lazyRetry(() => import("./pages/RebalancingSection.jsx"));
const PerformancePage = lazyRetry(() => import("./pages/PerformancePage.jsx"));
const MetricasPage = lazyRetry(() => import("./pages/MetricasPage.jsx"));
const RelatoriosPage = lazyRetry(() => import("./pages/RelatoriosPage.jsx"));
const AportesPage = lazyRetry(() => import("./pages/AportesPage.jsx"));
const MetasPage = lazyRetry(() => import("./pages/MetasPage.tsx"));
const CalcIRPage = lazyRetry(() => import("./pages/CalcIRPage.jsx"));
const InformeIRPage = lazyRetry(() => import("./pages/InformeIRPage.jsx"));
const DocumentosPage = lazyRetry(() => import("./pages/DocumentosPage.tsx"));
const AposentadoriaPage = lazyRetry(() => import("./pages/AposentadoriaPage.tsx"));
const EducacaoPage = lazyRetry(() => import("./pages/EducacaoPage.jsx"));
const MovimentacoesPage = lazyRetry(() => import("./pages/MovimentacoesPage.jsx"));
const PerfilPage = lazyRetry(() => import("./pages/PerfilPage.jsx"));
const CustosPage = lazyRetry(() => import("./pages/CustosPage.jsx"));
const DividendosPage = lazyRetry(() => import("./pages/DividendosPage.tsx"));
const CenariosPage = lazyRetry(() => import("./pages/CenariosPage.jsx"));
const PatrimonioTotalPage = lazyRetry(() => import("./pages/PatrimonioTotalPage.tsx"));
const PlanejamentoSucessorioPage = lazyRetry(
  () => import("./pages/PlanejamentoSucessorioPage.tsx")
);
const SimuladorTributarioPage = lazyRetry(() => import("./pages/SimuladorTributarioPage.tsx"));

const AlocacaoPage = lazyRetry(() => import("./pages/AlocacaoPage.jsx"));
const HedgeRecommendationPage = lazyRetry(() => import("./pages/HedgeRecommendationPage.jsx"));
const ScheduleDeliveryPage = lazyRetry(() => import("./pages/ScheduleDeliveryPage.jsx"));
const LiabilityAmortizationPage = lazyRetry(() => import("./pages/LiabilityAmortizationPage.jsx"));
const PrivacyPage = lazyRetry(() => import("./pages/PrivacyPage.tsx"));
const TradeProposalApproval = lazyRetry(() => import("./pages/TradeProposalApproval.tsx"));
const ClientTrackerPage = lazyRetry(() => import("./admin/ClientTrackerPage.tsx"));
const MeetingPrepPage = lazyRetry(() => import("./admin/MeetingPrepPage.tsx"));
const AdminRealocadorPage = lazyRetry(() => import("./admin/AdminRealocadorPage.tsx"));

// AdminPanel — clientes nunca baixam esse chunk
const AdminPanel = lazyRetry(() => import("./admin/AdminPanel.jsx"));

// ── Inject global CSS (skeleton + mobile) ────────────────
injectGlobalStyles();

// ═══════════════════════════════════════════════════════
// NOTA DE SEGURANÇA – PRODUÇÃO
// ═══════════════════════════════════════════════════════
// Autenticação é gerida pelo Supabase Auth (senhas bcrypt,
// JWT com expiração de 8h, RLS no banco de dados).
// Nenhuma senha ou dado sensível está presente no código-fonte.

// ═══════════════════════════════════════════════════════
// APP ROOT – SUPABASE SESSION MANAGEMENT
// ═══════════════════════════════════════════════════════
function AppInner() {
  const [session, setSession] = useState(undefined); // undefined=loading, null=logged out
  const [profile, setProfile] = useState(null);
  const [dark, setDark] = useState(() => {
    const stored = localStorage.getItem("lpa_dark");
    if (stored !== null) return stored !== "false";
    return detectSystemTheme(); // first visit: respect OS preference
  });
  // ── Máquina de estados do fluxo de primeiro acesso ───
  // "init"              → aguardando verificação (profile ainda não carregou)
  // "onboarding"        → wizard de suitability (pulável)
  // "awaiting_approval" → cliente preencheu, aguardando aprovação do admin
  // "upload"            → prompt de upload de extratos (pulável)
  // "ready"             → portal normal
  // "error"             → falha ao carregar perfil (sem conexão, etc.)
  const [flowStep, setFlowStep] = useState("init");
  const [loadError, setLoadError] = useState(null); // mensagem de erro de loadProfile
  const [clientSuit, setClientSuit] = useState(null); // dados iniciais do wizard
  const [pwdRecovery, setPwdRecovery] = useState(false); // modo redefinição de senha via link de e-mail
  const [sessionAlert, setSessionAlert] = useState(false); // sessão expirada enquanto usava o portal
  const [onboardingSaved, setOnboardingSaved] = useState(false); // toast de suitability salvo (fix Bug #310: hook movido antes dos early returns)
  const [showLGPDConsent, setShowLGPDConsent] = useState(false); // LGPD consent banner visibility
  const [totpVerified, setTotpVerified] = useState(false); // 2FA post-login verification passed
  const onboardingTimerRef = useRef(null);
  const T = dark ? DARK : LIGHT;

  // D3: Apply theme CSS variables whenever dark/light changes
  useEffect(() => {
    applyThemeCSSVars(T, dark);
  }, [dark, T]);

  // Cleanup do timer de onboarding ao desmontar
  useEffect(() => {
    return () => {
      if (onboardingTimerRef.current) clearTimeout(onboardingTimerRef.current);
    };
  }, []);

  // ── Carrega perfil e decide flowStep ─────────────────
  // Chamado APENAS em SIGNED_IN — nunca em USER_UPDATED (para não resetar o fluxo no meio do onboarding)
  const loadProfile = async (userId) => {
    setLoadError(null);
    try {
      const data = await fetchProfile(userId);
      setProfile(data);
      logAudit("login_success", { role: data?.role }).catch((e) =>
        logWarn("App", e.message, { context: "login_success audit" })
      );

      if (data?.role === "client" && data?.client_id) {
        const remoteSuit = await fetchSuitability(data.client_id).catch((err) => {
          logWarn("App", "Suitability fetch failed", { error: err?.message });
          return null;
        });
        setClientSuit(remoteSuit || null);

        // Sprint 10: onboarding_done é a ÚNICA fonte de verdade para decidir
        // se o wizard deve ser exibido. Clientes com onboarding_done=false devem
        // preencher o kit completo, independente de já possuírem registro de suitability.
        // Isso permite forçar re-onboarding ao resetar a flag no banco.
        const suitStatus = remoteSuit?.data?.status || remoteSuit?.status || null;
        const approvedFlag = remoteSuit?.data?.approved || remoteSuit?.approved || false;
        const isApproved = suitStatus === "aprovado" || approvedFlag === true;
        const onboardingDone = data.onboarding_done === true;

        // Check if user has given LGPD consent
        const { data: consentData } = await supabase
          .from("consent_records")
          .select("id")
          .eq("user_id", userId)
          .eq("granted", true)
          .gte("granted_at", new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString()) // within 1 year
          .limit(1);

        // Verificar consentimento: Supabase primeiro, localStorage como fallback
        let hasConsent = consentData && consentData.length > 0;
        if (!hasConsent) {
          // Fallback: verificar se já salvou no localStorage (caso upsert anterior
          // tenha falhado ou tabela consent_records ainda não exista)
          try {
            const stored = localStorage.getItem("lgpd_consent");
            if (stored) {
              const parsed = JSON.parse(stored);
              const storedTime = new Date(parsed.timestamp).getTime();
              const oneYearMs = 365 * 24 * 60 * 60 * 1000;
              if (Date.now() - storedTime < oneYearMs) hasConsent = true;
            }
          } catch {
            // Ignorar erro de parsing do localStorage
          }
        }
        if (!hasConsent) {
          setShowLGPDConsent(true);
        }

        // Flow: onboarding → awaiting_approval → (aprovado) → ready
        // Sprint 10: qualquer status não-aprovado bloqueia no awaiting_approval.
        if (!onboardingDone) {
          setFlowStep("onboarding");
        } else if (!isApproved) {
          setFlowStep("awaiting_approval");
        } else {
          setFlowStep("ready");
        }
      } else {
        // Admin, advisor ou perfil sem client_id → vai direto para o portal / tela de configuração
        setFlowStep("ready");
      }
    } catch (e) {
      logWarn("App", e.message, { context: "fetch profile" });
      // Nunca deixa profile=null com flowStep="ready" — isso causaria loading infinito.
      // Mostra tela de erro com opção de tentar novamente.
      setLoadError(e.message || "Falha de conexão com o servidor.");
      setFlowStep("error");
    }
  };

  // ── Detecta sessão atual e mudanças de auth ───────────
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session?.user?.id) loadProfile(session.user.id);
      else setSession(null);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);

      if (event === "PASSWORD_RECOVERY") {
        // Usuário clicou no link de redefinição de senha → mostra tela de nova senha imediatamente
        setPwdRecovery(true);
        return;
      }

      if (event === "SIGNED_IN") {
        // Login normal (ou retorno do link de reset) → recarrega perfil e flowStep
        if (session?.user?.id) loadProfile(session.user.id);
        return;
      }

      if (event === "TOKEN_REFRESHED") {
        // Token renovado silenciosamente — nada a fazer
        return;
      }

      if (!session) {
        // Logout / sessão expirada → limpa tudo
        // Se havia um perfil carregado, é sessão expirada (não logout voluntário)
        setProfile((prev) => {
          if (prev) setSessionAlert(true); // avisa que sessão expirou
          return null;
        });
        setFlowStep("init");
        setPwdRecovery(false);
        setTotpVerified(false);
      }
      // USER_UPDATED (mudança de senha, etc.) → setSession já foi chamado acima; flowStep é preservado
    });

    return () => subscription.unsubscribe();
  }, []);

  // ── Logout ────────────────────────────────────────────
  const handleLogout = async () => {
    await logAudit("logout", {}).catch((e) => console.warn("[audit] logout:", e.message));
    await signOut();
    setTotpVerified(false);
  };

  // ══════════════════════════════════════════════════════
  // RENDERIZAÇÃO — ordem importa (guards do mais restrito para o menos)
  // ══════════════════════════════════════════════════════

  // 1. Verificando sessão inicial
  if (session === undefined) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#0B2447",
          fontFamily: "'Inter',sans-serif",
        }}
      >
        <div style={{ textAlign: "center", color: "rgba(255,255,255,0.6)" }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 11,
              background: "#C9A227",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 22,
              fontWeight: 900,
              color: "#0B2447",
              margin: "0 auto 16px",
            }}
          >
            L
          </div>
          <div style={{ fontSize: 13 }}>Verificando sessão...</div>
        </div>
      </div>
    );
  }

  // 2. Não autenticado → tela de login
  if (!session)
    return <LoginPage sessionExpired={sessionAlert} onDismiss={() => setSessionAlert(false)} />;

  // 2b. Pack C — rota pública (com sessão) /trade-proposal/:id
  // Cliente acessa via link assinado do AAI; renderiza standalone, fora do shell.
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/trade-proposal/")) {
    return (
      <Suspense fallback={null}>
        <TradeProposalApproval T={T} />
      </Suspense>
    );
  }

  // 3. Redefinição de senha via link de e-mail (PASSWORD_RECOVERY)
  if (pwdRecovery) {
    return <ChangePasswordScreen onDone={() => setPwdRecovery(false)} T={T} />;
  }

  // 4a. Falha ao carregar perfil → tela de erro com retry
  if (flowStep === "error") {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#EEF2F7",
          fontFamily: "'Inter',sans-serif",
          padding: 24,
        }}
      >
        <div
          style={{
            background: "white",
            borderRadius: 20,
            padding: 48,
            maxWidth: 420,
            textAlign: "center",
            boxShadow: "0 8px 32px rgba(0,0,0,0.10)",
          }}
        >
          <div style={{ fontSize: 40, marginBottom: 16 }}>⚠️</div>
          <h2 style={{ color: "#1E3A5F", margin: "0 0 10px", fontSize: 18, fontWeight: 800 }}>
            Erro ao carregar portal
          </h2>
          <p style={{ color: "#64748B", fontSize: 13, lineHeight: 1.7, margin: "0 0 8px" }}>
            Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.
          </p>
          {loadError && (
            <p
              style={{
                color: "#94A3B8",
                fontSize: 11,
                fontFamily: "monospace",
                margin: "0 0 24px",
                wordBreak: "break-all",
              }}
            >
              {loadError}
            </p>
          )}
          <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            <button
              onClick={() => {
                setFlowStep("init");
                loadProfile(session.user.id);
              }}
              style={{
                background: "#1E3A5F",
                color: "white",
                border: "none",
                borderRadius: 10,
                padding: "11px 24px",
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Tentar novamente
            </button>
            <button
              onClick={handleLogout}
              style={{
                background: "transparent",
                color: "#64748B",
                border: "1.5px solid #E2E8F0",
                borderRadius: 10,
                padding: "11px 24px",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Sair
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 4b. Perfil ainda carregando
  if (!profile || flowStep === "init") {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#EEF2F7",
          fontFamily: "'Inter',sans-serif",
        }}
      >
        <div style={{ textAlign: "center", color: "#475569" }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: "#C9A227",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 20,
              fontWeight: 900,
              color: "#0B2447",
              margin: "0 auto 14px",
            }}
          >
            L
          </div>
          <div style={{ fontSize: 13 }}>Carregando seu perfil...</div>
        </div>
      </div>
    );
  }

  // 5. Admin → painel administrativo (lazy — carregado sob demanda)
  //    Aceita "admin" e "super_admin" (Chinese Wall: super_admin vê tudo)
  if (profile.role === "admin" || profile.role === "super_admin") {
    // 5a. Sub-rota do Client Tracker — /admin/clientes/:id/tracker
    // Standalone fora do shell do AdminPanel (foco total no cliente).
    const trackerMatch =
      typeof window !== "undefined" &&
      window.location.pathname.match(/^\/admin\/clientes\/([^/]+)\/tracker$/);
    if (trackerMatch) {
      return (
        <Suspense
          fallback={
            <div style={{ padding: 24, color: "#64748b" }}>Carregando tracker do cliente…</div>
          }
        >
          <ClientTrackerPage clientIdOverride={trackerMatch[1]} />
        </Suspense>
      );
    }

    // 5b. Sub-rota do Meeting Prep — /admin/clientes/:id/meeting-prep
    // Briefing print-ready pra reunião mensal (Sprint 2).
    const meetingMatch =
      typeof window !== "undefined" &&
      window.location.pathname.match(/^\/admin\/clientes\/([^/]+)\/meeting-prep$/);
    if (meetingMatch) {
      return (
        <Suspense
          fallback={
            <div style={{ padding: 24, color: "#64748b" }}>Gerando briefing da reunião…</div>
          }
        >
          <MeetingPrepPage clientIdOverride={meetingMatch[1]} />
        </Suspense>
      );
    }

    // 5c. Sub-rota do Realocador — /admin/clientes/:id/realocador
    // Análise + simulator + proposal generator (substitui AdminRebalancingEngine V1).
    const realocadorMatch =
      typeof window !== "undefined" &&
      window.location.pathname.match(/^\/admin\/clientes\/([^/]+)\/realocador$/);
    if (realocadorMatch) {
      return (
        <Suspense
          fallback={<div style={{ padding: 24, color: "#64748b" }}>Carregando realocador…</div>}
        >
          <AdminRealocadorPage clientIdOverride={realocadorMatch[1]} />
        </Suspense>
      );
    }

    return (
      <Suspense
        fallback={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100vh",
              fontFamily: "sans-serif",
              color: "#475569",
            }}
          >
            Carregando painel administrativo…
          </div>
        }
      >
        <AdminPanel
          userName={
            typeof profile.full_name === "string"
              ? profile.full_name
              : String(session.user.email || "")
          }
          onLogout={handleLogout}
          T={T}
        />
      </Suspense>
    );
  }

  // ── Variáveis do cliente (usadas nos steps abaixo) ────
  const clientId = profile.client_id; // NUNCA usar fallback de outro cliente
  // FIX #310: blindar — full_name pode ser objeto se profiles tiver JSONB inesperado
  const rawName = profile.full_name;
  const clientName =
    typeof rawName === "string" && rawName ? rawName : String(session.user.email || "");

  // 6. Troca de senha OBRIGATÓRIA (bloqueante) — primeiro acesso via Admin
  // A flag `must_change_password` é definida pela Edge Function create-client
  if (session.user?.user_metadata?.must_change_password) {
    return (
      <ChangePasswordScreen
        onDone={async () => {
          // Safety net duplo:
          // 1. Edge Function remove flag must_change_password via auth.admin (caso
          //    confirm-password-change já removeu mas TOKEN_REFRESHED não chegou).
          // 2. Força refresh da sessão para obter user_metadata atualizado.
          await invokeRemoveMustChangePassword().catch((e) =>
            logWarn("App", e.message, { context: "removeMustChangePassword" })
          );
          try {
            const {
              data: { session: s },
            } = await supabase.auth.refreshSession();
            if (s) setSession(s);
            else window.location.reload();
          } catch (e) {
            // refreshSession falhou — recarrega pra re-hidratar do zero
            // (evita usuário preso na tela de troca de senha após sucesso)
            logWarn("App", e?.message ?? "refreshSession failed", {
              context: "refreshSession",
            });
            window.location.reload();
          }
        }}
        T={T}
      />
    );
  }

  // 6b. 2FA OBRIGATÓRIO — enrollment (admin/advisor sem 2FA ativo)
  // CVM 21 exige 2FA para perfis com acesso fiduciário
  const totpEnforced = profile.totp_enforced || ["admin", "advisor"].includes(profile.role);
  const totpEnabled = profile.totp_enabled === true;

  if (totpEnforced && !totpEnabled) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: `linear-gradient(135deg, ${T.navy} 0%, ${T.navy2} 100%)`,
          padding: 24,
        }}
      >
        <TwoFactorSetup
          T={T}
          userId={session.user.id}
          userEmail={profile.email}
          onComplete={async () => {
            // Refresh profile to get updated totp_enabled flag
            const data = await fetchProfile(session.user.id);
            if (data) setProfile(data);
          }}
        />
        <TwoFactorEscapeHatch onLogout={handleLogout} />
      </div>
    );
  }

  // 6c. 2FA VERIFICAÇÃO — post-login TOTP check (usuário com 2FA ativo)
  if (totpEnabled && !totpVerified) {
    return (
      <>
        <TwoFactorVerify
          T={T}
          onVerify={async (code) => {
            const { data, error: fnErr } = await supabase.functions.invoke("verify-totp", {
              body: { code, user_id: session.user.id },
            });
            if (fnErr) throw fnErr;
            if (!data?.valid) throw new Error(data?.error || "Código inválido");
            setTotpVerified(true);
          }}
          onUseBackup={() => {
            // Backup code flow — uses same verify-totp endpoint with backup_code flag
            const backupCode = prompt("Digite seu código de backup:");
            if (!backupCode) return;
            supabase.functions
              .invoke("verify-totp", {
                body: { code: backupCode, user_id: session.user.id, backup_code: true },
              })
              .then(({ data, error: fnErr }) => {
                if (fnErr || !data?.valid) {
                  alert("Código de backup inválido.");
                  return;
                }
                setTotpVerified(true);
              });
          }}
        />
        <TwoFactorEscapeHatch onLogout={handleLogout} />
      </>
    );
  }

  // 7. Perfil sem client_id (conta ainda sendo configurada)
  if (!clientId) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#EEF2F7",
          fontFamily: "'Inter',sans-serif",
          padding: 24,
        }}
      >
        <div
          style={{
            background: "white",
            borderRadius: 20,
            padding: 48,
            maxWidth: 440,
            textAlign: "center",
            boxShadow: "0 8px 32px rgba(0,0,0,0.10)",
          }}
        >
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 13,
              background: "#FEF3C7",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 28,
              margin: "0 auto 18px",
            }}
          >
            ⚙️
          </div>
          <h2 style={{ color: "#1E3A5F", margin: "0 0 10px", fontSize: 20, fontWeight: 800 }}>
            Perfil em configuração
          </h2>
          <p style={{ color: "#64748B", fontSize: 14, lineHeight: 1.7, margin: "0 0 24px" }}>
            Sua conta ainda está sendo configurada pelo assessor LPA.
            <br />
            Em breve você receberá um e-mail com suas credenciais de acesso.
          </p>
          <p style={{ color: "#94A3B8", fontSize: 12, margin: "0 0 24px" }}>
            Dúvidas? Entre em contato:
            <br />
            <strong style={{ color: "#1E3A5F" }}>contato@lpawealth.com.br</strong>
          </p>
          <button
            onClick={handleLogout}
            style={{
              background: "#1E3A5F",
              color: "white",
              border: "none",
              borderRadius: 10,
              padding: "12px 28px",
              fontSize: 14,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Sair
          </button>
        </div>
      </div>
    );
  }

  // Helper: avança para a etapa de awaiting_approval após completar o onboarding.
  // Sprint 10: preenchimento é obrigatório — não existe mais caminho de "pular".
  // Propaga exceção se falhar: o OnboardingWizard captura e exibe a mensagem.
  const goToUpload = async (suitData) => {
    if (!suitData) {
      // Sprint 10: skip desabilitado — se chamado sem dados, ignora silenciosamente.
      logWarn("App", "goToUpload chamado sem suitData — ignorando (skip desabilitado)");
      return;
    }

    // ── Salvar suitability ────────────────────────────────────────────
    await upsertSuitability(clientId, suitData);

    // Histórico de suitability (melhor esforço — nunca bloqueia)
    if (suitData.perfilResult && suitData.scoreSuit) {
      logSuitabilityHistory(clientId, {
        perfil: suitData.perfilResult,
        score: suitData.scoreSuit,
        scoreMax: suitData.scoreMax || 25,
        status: suitData.status || "parcial",
      }).catch((e) => logWarn("App", e.message, { context: "logSuitabilityHistory" }));
    }

    setOnboardingSaved(true); // mostra toast "Perfil salvo com sucesso!"

    // Marca onboarding como concluído para logins futuros
    await markOnboardingDone().catch((e) =>
      logWarn("App", e.message, { context: "markOnboardingDone after fill" })
    );

    // ── Notificar admin que há novo kit para revisão (melhor esforço) ──
    // Usa client_id = "admin" como canal de notificação do admin.
    createNotification(
      "admin",
      "onboarding_review",
      "Novo Kit de Onboarding",
      `O cliente ${clientName} completou o kit de onboarding e aguarda aprovação.`
    ).catch((e) => logWarn("App", "Falha ao notificar admin", { error: e?.message }));

    // Após preencher, aguarda aprovação do admin antes de prosseguir
    onboardingTimerRef.current = setTimeout(() => setFlowStep("awaiting_approval"), 900);
  };

  // 8. ONBOARDING — wizard de suitability (obrigatório)
  // Sprint 10: preenchimento do kit é condição precedente — não é mais pulável.
  // O cliente DEVE completar todas as etapas antes de acessar o portal.
  if (flowStep === "onboarding") {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "linear-gradient(135deg,#0B2447 0%,#1a3a6e 100%)",
          fontFamily: "'Inter',sans-serif",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: 20,
        }}
      >
        {/* Toast de sucesso ao salvar onboarding */}
        {onboardingSaved && (
          <div
            style={{
              position: "fixed",
              top: 20,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 9999,
              background: "#16A34A",
              color: "white",
              borderRadius: 12,
              padding: "12px 24px",
              fontSize: 14,
              fontWeight: 700,
              boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            ✅ Perfil salvo com sucesso!
          </div>
        )}

        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 12,
              background: "#C9A227",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 24,
              fontWeight: 900,
              color: "#0B2447",
              margin: "0 auto 12px",
            }}
          >
            L
          </div>
          <p style={{ color: "rgba(255,255,255,0.55)", fontSize: 12, margin: 0 }}>
            Bem-vindo ao portal LPA Wealth · Completar cadastro obrigatório
          </p>
        </div>

        <div
          style={{
            width: "100%",
            maxWidth: 700,
            background: "#fff",
            borderRadius: 20,
            overflow: "hidden",
            boxShadow: "0 24px 64px rgba(0,0,0,0.35)",
          }}
        >
          <OnboardingWizard
            clientId={clientId}
            clientName={clientName}
            initialData={clientSuit}
            onSave={async (id, data) => goToUpload(data)}
            onClose={null}
            T={LIGHT}
          />
        </div>
      </div>
    );
  }

  // 8b. AGUARDANDO APROVAÇÃO — admin precisa aprovar onboarding
  if (flowStep === "awaiting_approval") {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "linear-gradient(135deg,#0B2447 0%,#1a3a6e 100%)",
          fontFamily: "'Inter',sans-serif",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: 20,
          textAlign: "center",
        }}
      >
        <div
          style={{
            background: "rgba(255,255,255,0.06)",
            borderRadius: 20,
            padding: "48px 40px",
            maxWidth: 500,
            backdropFilter: "blur(12px)",
            border: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
          <h2
            style={{
              color: "#F6F3EE",
              fontSize: 22,
              fontWeight: 800,
              margin: "0 0 12px",
            }}
          >
            Onboarding Enviado!
          </h2>
          <p
            style={{
              color: "rgba(246,243,238,0.7)",
              fontSize: 14,
              lineHeight: 1.6,
              margin: "0 0 24px",
            }}
          >
            Seu questionário de onboarding foi preenchido com sucesso e enviado para revisão. Seu
            consultor irá analisar as respostas e aprovar seu cadastro em breve.
          </p>
          <div
            style={{
              background: "rgba(201,162,39,0.15)",
              border: "1px solid rgba(201,162,39,0.3)",
              borderRadius: 12,
              padding: "14px 20px",
              marginBottom: 24,
            }}
          >
            <p
              style={{
                color: "#C9A227",
                fontSize: 13,
                fontWeight: 600,
                margin: 0,
              }}
            >
              🔒 Aguardando aprovação do consultor
            </p>
          </div>
          <button
            onClick={() => {
              signOut();
            }}
            style={{
              padding: "12px 28px",
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.2)",
              background: "transparent",
              color: "rgba(255,255,255,0.7)",
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            Sair
          </button>
        </div>
      </div>
    );
  }

  // 9. UPLOAD — prompt de envio de extratos (pulável)
  if (flowStep === "upload") {
    return (
      <UploadPromptScreen
        clientName={clientName}
        clientId={clientId}
        userId={session.user.id}
        onDone={() => setFlowStep("ready")}
        suitabilityDone={onboardingSaved} // ← stepper reflete se preencheu ou pulou
        T={T}
      />
    );
  }

  // 10. PORTAL — acesso normal (todos os acessos subsequentes chegam aqui)
  const handleSetDark = (val) => {
    setDark(val);
    localStorage.setItem("lpa_dark", String(val));
    // CSS vars + body class + transition handled by useEffect → applyThemeCSSVars
  };
  return (
    <>
      {showLGPDConsent && session?.user?.id && (
        <LGPDConsentBanner
          T={T}
          userId={session.user.id}
          onComplete={() => setShowLGPDConsent(false)}
        />
      )}
      <a
        href="#main-content"
        style={{
          position: "absolute",
          left: "-9999px",
          top: "auto",
          width: "1px",
          height: "1px",
          overflow: "hidden",
          zIndex: 9999,
        }}
        onFocus={(e) => {
          e.target.style.position = "fixed";
          e.target.style.left = "16px";
          e.target.style.top = "16px";
          e.target.style.width = "auto";
          e.target.style.height = "auto";
          e.target.style.overflow = "visible";
          e.target.style.padding = "12px 24px";
          e.target.style.background = T.accent;
          e.target.style.color = T.accentText;
          e.target.style.borderRadius = "8px";
          e.target.style.fontWeight = "700";
          e.target.style.fontSize = "14px";
          e.target.style.textDecoration = "none";
          e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.3)";
        }}
        onBlur={(e) => {
          e.target.style.position = "absolute";
          e.target.style.left = "-9999px";
          e.target.style.width = "1px";
          e.target.style.height = "1px";
          e.target.style.overflow = "hidden";
        }}
      >
        Pular para conteúdo principal
      </a>
      {/* Onboarding tour — exibe no primeiro acesso, persiste em localStorage */}
      <Suspense fallback={null}>
        <LpaOnboardingTour />
      </Suspense>
      <ClientLayout
        clientId={clientId}
        clientName={clientName}
        profile={profile}
        userEmail={session.user.email}
        onLogout={handleLogout}
        dark={dark}
        setDark={handleSetDark}
        pages={({ hasSnapshot }) => ({
          dashboard: hasSnapshot ? <DashboardPage /> : <EmptyStateDashboard />,
          carteira: hasSnapshot ? (
            <div>
              <CarteiraPage />
              <RebalancingSection />
            </div>
          ) : (
            <EmptyStatePage label="Carteira" />
          ),
          performance: hasSnapshot ? <PerformancePage /> : <EmptyStatePage label="Performance" />,
          metricas: hasSnapshot ? <MetricasPage /> : <EmptyStatePage label="Métricas" />,
          relatorios: <RelatoriosPage />,
          aportes: hasSnapshot ? <AportesPage /> : <EmptyStatePage label="Aportes e Projeções" />,
          movimentacoes: hasSnapshot ? (
            <MovimentacoesPage />
          ) : (
            <EmptyStatePage label="Movimentações" />
          ),
          metas: <MetasPage />,
          calcir: <CalcIRPage />,
          informeir: <InformeIRPage />,
          documentos: <DocumentosPage />,
          aposentadoria: hasSnapshot ? (
            <AposentadoriaPage />
          ) : (
            <EmptyStatePage label="Aposentadoria" />
          ),
          custos: <CustosPage />,
          dividendos: hasSnapshot ? <DividendosPage /> : <EmptyStatePage label="Dividendos" />,
          alocacao: hasSnapshot ? <AlocacaoPage /> : <EmptyStatePage label="Alocação" />,
          cenarios: hasSnapshot ? <CenariosPage /> : <EmptyStatePage label="Cenários" />,
          "patrimonio-total": <PatrimonioTotalPage />,
          sucessao: <PlanejamentoSucessorioPage />,
          "simulador-tributario": <SimuladorTributarioPage />,

          hedge: hasSnapshot ? (
            <HedgeRecommendationPage />
          ) : (
            <EmptyStatePage label="Hedge & Proteção" />
          ),
          "schedule-delivery": <ScheduleDeliveryPage />,
          amortizacao: <LiabilityAmortizationPage />,
          educacao: <EducacaoPage />,
          perfil: <PerfilPage />,
          privacidade: <PrivacyPage />,
        })}
      />
    </>
  );
}

// ═══════════════════════════════════════════════════════
// TwoFactorEscapeHatch — botão "Sair" flutuante nas telas 2FA
// Garante que o usuário nunca fica preso na verificação/setup 2FA
// (ex: erro de rede no verify-totp, app indisponível).
// ═══════════════════════════════════════════════════════
function TwoFactorEscapeHatch({ onLogout }) {
  return (
    <button
      type="button"
      onClick={onLogout}
      style={{
        position: "fixed",
        top: 16,
        right: 16,
        zIndex: 9999,
        padding: "7px 14px",
        borderRadius: 8,
        border: "1px solid rgba(255,255,255,0.25)",
        background: "rgba(0,0,0,0.35)",
        color: "#fff",
        fontSize: 12,
        fontWeight: 600,
        fontFamily: "'Inter',sans-serif",
        cursor: "pointer",
        backdropFilter: "blur(4px)",
      }}
    >
      Sair
    </button>
  );
}

// ═══════════════════════════════════════════════════════
// APP ROOT — wrapped with ErrorBoundary
// ═══════════════════════════════════════════════════════
export default function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <AppInner />
      </ErrorBoundary>
    </BrowserRouter>
  );
}
